import { useMemo } from 'react'
import type { Player, GamePhase, GameState } from '@/game/types'

interface PlayerViewProps {
  player: Player
  state: GameState
  onPhase: (phase: GamePhase) => void
  onAriChoice?: (choice: 'staff' | 'debut') => void
}

const clean = (value: string) => value.replaceAll('—', '-').replaceAll('’', "'")

function SectionHeading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return <div className="section-heading"><div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2></div></div>
}

const ABILITY_CATEGORIES = {
  Physical: ['speed', 'acceleration', 'endurance', 'strength', 'agility', 'leap', 'recovery', 'durability'] as const,
  'Football Skills': ['kicking', 'handball', 'marking', 'groundBall', 'tackling', 'ruckWork', 'goalSense', 'defensiveCraft', 'contestedAbility'] as const,
  'Football Intelligence': ['positioning', 'decisionMaking', 'anticipation', 'spatialAwareness', 'tacticalDiscipline', 'adaptability', 'composure'] as const,
  Character: ['professionalism', 'coachability', 'competitiveness', 'resilience', 'loyalty', 'ambition', 'selflessness', 'leadership', 'consistency', 'bigGameTemperament'] as const,
} as const

const POSITION_LABELS: Record<Player['position'], string> = {
  FB: 'Full Back',
  HB: 'Half Back',
  C: 'Centre',
  HF: 'Half Forward',
  F: 'Forward',
  FOL: 'Follower',
  INT: 'Interchange',
  EMG: 'Emergency',
}

const ABILITY_GRADES: Array<Player['abilities'][keyof Player['abilities']]> = [
  'Limited',
  'Below Standard',
  'League Standard',
  'Above Standard',
  'Strong',
  'Elite',
  'Exceptional',
]

const ABILITY_COLOR: Record<Player['abilities'][keyof Player['abilities']], string> = {
  Limited: 'var(--bad)',
  'Below Standard': 'var(--warn)',
  'League Standard': 'var(--ink)',
  'Above Standard': 'var(--good)',
  Strong: 'var(--good)',
  Elite: 'var(--accent)',
  Exceptional: 'var(--accent)',
}

function gradeToPct(grade: Player['abilities'][keyof Player['abilities']]): number {
  const idx = ABILITY_GRADES.indexOf(grade)
  return Math.round(((idx + 0.5) / ABILITY_GRADES.length) * 100)
}

function formatAbilityKey(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())
}

function renderFace(player: Player) {
  const v = player.visual
  if (!v) {
    return (
      <svg viewBox="0 0 100 100" width="100" height="100">
        <circle cx="50" cy="50" r="45" fill="var(--gold)" opacity="0.2" />
        <text x="50" y="55" textAnchor="middle" fill="var(--gold)" fontSize="28" fontWeight="700">
          {player.name.charAt(0)}
        </text>
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 100 100" width="100" height="100" role="img" aria-label={v.description}>
      <defs>
        <radialGradient id={`face-${player.id}`} cx="50%" cy="30%" r="50%">
          <stop offset="0%" stopColor="var(--gold)" stopOpacity="0.15" />
          <stop offset="70%" stopColor="var(--panel-2)" stopOpacity="1" />
        </radialGradient>
      </defs>
      <circle cx="50" cy="35" r="30" fill={`url(#face-${player.id})`} />
      <ellipse cx="40" cy="30" rx="6" ry="5" fill="var(--gold)" opacity="0.3" />
      <ellipse cx="60" cy="30" rx="6" ry="5" fill="var(--gold)" opacity="0.3" />
      <path d="M35 50 Q50 58 65 50" stroke="var(--gold)" strokeWidth="2" fill="none" opacity="0.4" />
      <text x="50" y="85" textAnchor="middle" fill="var(--gold)" fontSize="10" fontWeight="700" fontFamily="Barlow Condensed">
        {v.traits.slice(0, 2).join(' · ')}
      </text>
    </svg>
  )
}

function Meter({ label, value, max = 100, color = 'var(--gold)' }: { label: string; value: number; max?: number; color?: string }) {
  const pct = Math.max(0, Math.min(100, Math.round((value / max) * 100)))
  return (
    <div className="meter">
      <span>{label}</span>
      <div>
        <i style={{ width: `${pct}%`, background: color }} />
      </div>
      <strong>{value}</strong>
    </div>
  )
}

function AbilityRow({ abilityKey, grade }: { abilityKey: string; grade: Player['abilities'][keyof Player['abilities']] }) {
  const pct = gradeToPct(grade)
  const isPositive = ['Above Standard', 'Strong', 'Elite', 'Exceptional'].includes(grade)
  const isNegative = ['Limited', 'Below Standard'].includes(grade)
  return (
    <div className="ability-row">
      <span>{formatAbilityKey(abilityKey)}</span>
      <div className="ability-bar-wrap">
        <div className="ability-bar" style={{ width: `${pct}%`, background: ABILITY_COLOR[grade] }} />
      </div>
      <strong className={isPositive ? 'positive' : isNegative ? 'warning' : ''}>{grade}</strong>
    </div>
  )
}

function CategoryPanel({ title, abilities, player }: { title: string; abilities: readonly string[]; player: Player }) {
  return (
    <section className="panel ability-category">
      <SectionHeading eyebrow="Staff estimates" title={title} />
      <div className="ability-list">
        {abilities.map(key => (
          <AbilityRow key={key} abilityKey={key} grade={player.abilities[key as keyof Player['abilities']]} />
        ))}
      </div>
    </section>
  )
}

export function PlayerView({ player, state, onPhase, onAriChoice }: PlayerViewProps) {
  const devAlloc = useMemo(
    () => state.staffPlan.development.find(d => d.playerId === player.id),
    [state.staffPlan.development, player.id]
  )

  const hasSeniorExperience = player.development.seniorMatches > 0
  const isSelected = player.isSelected
  const positionLabel = POSITION_LABELS[player.position]
  const contractStatus = player.contractYears === 0 ? 'Out of contract' : `${player.contractYears} year${player.contractYears === 1 ? '' : 's'} remaining`
  const ariDebut = player.id === 'ari-dane' && state.decisions.D2.currentChoice === 'B'

  return (
    <div className="screen player-view-screen">
      <button className="back-link" type="button" onClick={() => onPhase('squad')}>
        Back to Squad
      </button>

      {/* Player Banner */}
      <div className="player-banner">
        <div className="guernsey">{renderFace(player)}</div>
        <div>
          <span className="eyebrow">
            {player.primaryRole} · Age {player.age} · {positionLabel}
          </span>
          <h1>{player.name}</h1>
          <p>
            {hasSeniorExperience
              ? `${player.development.seniorMatches} senior ${player.development.seniorMatches === 1 ? 'match' : 'matches'}`
              : 'Senior debut eligible'}
            {player.draftedSeason && ` · Drafted Season ${player.draftedSeason}`}
          </p>
        </div>
        <div className={`selection-stamp ${isSelected ? 'selected' : ''}`}>
          <span>{isSelected ? 'Selected' : player.position === 'EMG' ? 'Emergency' : 'Not selected'}</span>
          <strong>{player.position}</strong>
        </div>
      </div>

      <div className="player-grid">
        {/* Condition Panel */}
        <section className="panel condition">
          <SectionHeading
            eyebrow="Weekly condition"
            title={player.injury ? `Unavailable — ${player.injury.severity} ${player.injury.type}` : 'Available'}
          />
          {player.injury && (
            <div className="injury-summary">
              <strong>{player.injury.severity} {player.injury.type}</strong>
              <span>
                {player.injury.remainingWeeks} {player.injury.remainingWeeks === 1 ? 'week' : 'weeks'} remaining
                {player.injury.isRecurrence && ` · Recurrence #${player.injury.recurrenceCount}`}
              </span>
            </div>
          )}
          <Meter label="Fitness" value={player.fitness} />
          <Meter label="Form" value={player.form} />
          <Meter label="Morale" value={player.morale} />
          <Meter label="Fatigue" value={player.fatigue} color={player.fatigue > 70 ? 'var(--warn)' : 'var(--gold)'} />
          <div className="contract">
            <span>Contract</span>
            <strong>{contractStatus}</strong>
          </div>
          {player.injuryHistory && player.injuryHistory.length > 0 && (
            <details className="injury-history">
              <summary>Injury history ({player.injuryHistory.length})</summary>
              <ul>
                {player.injuryHistory.map(inj => (
                  <li key={inj.id}>
                    {inj.severity} {inj.type} · Season {inj.occurredSeason} Round {inj.occurredRound}{inj.isRecurrence && ' (recurrence)'}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>

        {/* Development Panel */}
        <section className="panel player-progression">
          <SectionHeading eyebrow="Persistent development" title={player.development.focus || 'No active focus'} />
          <div className="progression-forecast">
            <div>
              <span>Banked progress</span>
              <strong>{player.development.progress}</strong>
            </div>
            <div>
              <span>Next review window</span>
              <strong>{player.development.reviewWindow.min}–{player.development.reviewWindow.max}</strong>
            </div>
            <div>
              <span>Staff confidence</span>
              <strong>{player.development.staffConfidence}</strong>
            </div>
            <div>
              <span>Senior matches</span>
              <strong>{player.development.seniorMatches}</strong>
            </div>
            <div>
              <span>Role familiarity</span>
              <strong>{player.development.roleFamiliarity}%</strong>
            </div>
          </div>
          <Meter label="Role familiarity" value={player.development.roleFamiliarity} />
          <p>
            Current estimate: <strong>{player.abilities[player.development.nextAbility]}</strong> ({formatAbilityKey(player.development.nextAbility)}).{' '}
            Progress creates a review opportunity, not a guaranteed upgrade.
          </p>

          {devAlloc && (
            <div className="current-allocation">
              <strong>Current allocation:</strong> {devAlloc.points} pts → {devAlloc.focus} (Coach: {devAlloc.assistant})
            </div>
          )}

          {player.development.history.length > 0 && (
            <details className="progression-history">
              <summary>Development history ({player.development.history.length} entries)</summary>
              <div className="history-list">
                {[...player.development.history].reverse().map(item => (
                  <div key={item.round} className="history-entry">
                    <span>Round {item.round} · +{item.progressGained}{item.matchExposure ? ' · senior exposure' : ' · training only'}</span>
                    <p>{clean(item.summary)}</p>
                    {item.upgrade && (
                      <em>{formatAbilityKey(item.upgrade.ability)}: {item.upgrade.from} → {item.upgrade.to}</em>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Development Actions */}
          <div className="dev-actions">
            <h4>Development actions</h4>
            <div className="action-grid">
              <button
                type="button"
                className="text-button"
                onClick={() => onPhase('development')}
              >
                Adjust allocation
              </button>
              <button
                type="button"
                className="text-button"
                onClick={() => onPhase('team')}
              >
                {isSelected ? 'Modify selection' : 'Select for match day'}
              </button>
              <button
                type="button"
                className="text-button"
                onClick={() => onPhase('tactics')}
              >
                Role instructions
              </button>
            </div>
          </div>
        </section>

        {/* Ability Categories */}
        {Object.entries(ABILITY_CATEGORIES).map(([category, keys]) => (
          <CategoryPanel key={category} title={category} abilities={keys} player={player} />
        ))}

        <section className="panel confidence-note">
          <p>
            <strong>Estimate confidence: medium.</strong> Do not treat grades as fixed ceilings. Ranges narrow with senior exposure,
            medical assessment, and targeted research.
          </p>
        </section>

        {/* Football Department Opinions */}
        <section className="panel opinions">
          <SectionHeading eyebrow="Football department" title="Staff opinions" />
          {state.club.staff.map(member => (
            <blockquote key={member.id}>
              <p>"{clean(member.recommendation)}"</p>
              <footer>
                <strong>{member.name}</strong>
                <span>{member.role} · {member.philosophy}</span>
              </footer>
            </blockquote>
          ))}
          {player.staffConcern && (
            <div className="concern">
              <strong>Known concern</strong>
              <p>{clean(player.staffConcern)}</p>
            </div>
          )}
        </section>

        {/* Career History */}
        {player.clubHistory && player.clubHistory.length > 0 && (
          <section className="panel career-history">
            <SectionHeading eyebrow="Career" title="Club history" />
            <ul className="history-list">
              {player.clubHistory.map((entry, idx) => (
                <li key={idx}>
                  <strong>Season {entry.fromSeason}{entry.toSeason ? `–${entry.toSeason}` : '–present'}</strong> —{' '}
                  {entry.arrival === 'foundation'
                    ? 'Foundation player'
                    : entry.arrival === 'draft'
                    ? 'Drafted'
                    : 'Traded in'}
                  {entry.clubId !== 'kingsport' && ` from ${entry.clubId}`}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Visual Traits */}
        {player.visual && player.visual.traits.length > 0 && (
          <section className="panel visual-traits">
            <SectionHeading eyebrow="Profile" title="Observed traits" />
            <div className="traits-list">
              {player.visual.traits.map((trait, idx) => (
                <span key={idx} className="trait-tag">
                  {trait}
                </span>
              ))}
            </div>
            <p className="visual-desc">{clean(player.visual.description)}</p>
          </section>
        )}

        {player.id === 'ari-dane' && onAriChoice && (
          <section className="panel decision-panel">
            <SectionHeading eyebrow="D2 - Selection" title="Resolve the disagreement" />
            <label className={`choice ${!ariDebut ? 'chosen' : ''}`}>
              <input type="radio" name="ari-plan" checked={!ariDebut} onChange={() => onAriChoice('staff')} />
              <span><strong>D2-A - Retain staff plan</strong><small>Heath Mercer remains on interchange. Ari continues his development work as an emergency.</small></span>
              <em>Staff recommendation</em>
            </label>
            <label className={`choice ${ariDebut ? 'chosen override' : ''}`}>
              <input type="radio" name="ari-plan" checked={ariDebut} disabled={Boolean(player.injury)} onChange={() => onAriChoice('debut')} />
              <span><strong>D2-B - Select Ari Dane</strong><small>{player.injury ? `Unavailable: ${player.injury.severity} ${player.injury.type}, ${player.injury.remainingWeeks} ${player.injury.remainingWeeks === 1 ? 'week' : 'weeks'} remaining.` : 'Ari replaces Heath Mercer. Pace and senior exposure rise; tactical reliability falls.'}</small></span>
              <em>{player.injury ? 'Unavailable' : ariDebut ? 'Coach-locked' : 'Coach option'}</em>
            </label>
            {ariDebut && <button className="restore" type="button" onClick={() => onAriChoice('staff')}>Restore Staff Choice</button>}
          </section>
        )}
      </div>

      <div className="sticky-actions">
        <span>
          {player.id === 'ari-dane' && ariDebut
            ? `Coach override saved - Ari selected for Round ${state.round}`
            : isSelected
            ? `Selected for Round ${state.round}`
            : player.position === 'EMG'
            ? 'Named as emergency'
            : 'Not in match day squad'}
        </span>
        <button className="primary-action" type="button" onClick={() => onPhase('squad')}>
          Return to Squad
        </button>
      </div>
    </div>
  )
}